import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';

const PrivacyPolicy: React.FC = () => {
    useDocumentTitle('Gizlilik Politikası | CO-HR');
    const history = useHistory();

    return (
        <div className="min-h-screen bg-white font-sans">
            <header className="sticky top-0 bg-white/90 backdrop-blur-xl border-b border-gray-100 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <button onClick={() => history.push('/')} className="text-xl font-extrabold bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent focus:outline-none">
                        CO-HR
                    </button>
                    <button onClick={() => history.goBack()} className="text-sm text-gray-500 hover:text-gray-900 transition focus:outline-none">
                        &larr; Geri Dön
                    </button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Gizlilik Politikası</h1>
                <p className="text-gray-400 text-sm mb-10">Son güncelleme: 1 Nisan 2026</p>

                <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Giriş</h2>
                        <p>
                            CO-HR Performans Yönetim Sistemi ("Platform") olarak, kullanıcılarımızın gizliliğini korumayı taahhüt ediyoruz.
                            Bu Gizlilik Politikası, kişisel verilerinizin nasıl toplandığını, kullanıldığını, saklandığını ve korunduğunu açıklamaktadır.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Toplanan Veriler</h2>
                        <p>Platform üzerinden aşağıdaki kişisel veriler toplanabilmektedir:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li><strong>Kimlik Bilgileri:</strong> Ad, soyad, sicil numarası, T.C. kimlik numarası</li>
                            <li><strong>İletişim Bilgileri:</strong> E-posta adresi, telefon numarası</li>
                            <li><strong>Çalışan Bilgileri:</strong> Departman, ünvan, yönetici bilgisi, işe başlama tarihi</li>
                            <li><strong>Performans Verileri:</strong> Hedefler, OKR'lar, performans kartları, değerlendirme puanları, yetkinlik değerlendirmeleri</li>
                            <li><strong>Kullanım Verileri:</strong> Oturum bilgileri, IP adresi, tarayıcı bilgisi, erişim zamanları</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Verilerin Kullanım Amaçları</h2>
                        <p>Toplanan kişisel veriler aşağıdaki amaçlarla işlenmektedir:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>Performans değerlendirme süreçlerinin yürütülmesi</li>
                            <li>OKR ve hedef yönetimi hizmetlerinin sunulması</li>
                            <li>360° geri bildirim süreçlerinin yönetimi</li>
                            <li>Raporlama ve analitik hizmetlerinin sağlanması</li>
                            <li>Kullanıcı hesaplarının oluşturulması ve yönetimi</li>
                            <li>Platform güvenliğinin sağlanması</li>
                            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Verilerin Saklanması ve Güvenliği</h2>
                        <p>
                            Kişisel verileriniz, endüstri standardı güvenlik önlemleri ile korunmaktadır. Verileriniz şifrelenmiş
                            bağlantılar (SSL/TLS) üzerinden iletilir ve güvenli sunucularda saklanır. Erişim kontrolleri, yetkilendirme
                            mekanizmaları ve düzenli güvenlik denetimleri ile verilerinizin güvenliği sağlanmaktadır.
                        </p>
                        <p className="mt-2">
                            Kişisel verileriniz, işleme amacının gerektirdiği süre boyunca ve yasal saklama sürelerine uygun olarak muhafaza edilir.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Verilerin Paylaşımı</h2>
                        <p>
                            Kişisel verileriniz, açık rızanız olmaksızın üçüncü taraflarla paylaşılmaz. Ancak aşağıdaki durumlarda
                            paylaşım yapılabilir:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>Yasal zorunluluklar kapsamında yetkili kamu kurum ve kuruluşlarına</li>
                            <li>Platformun teknik altyapısını sağlayan hizmet sağlayıcılara (veri işleyenler)</li>
                            <li>İşveren kuruluşunuzun yetkili yöneticilerine (performans verileri kapsamında)</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Çerezler (Cookies)</h2>
                        <p>
                            Platform, kullanıcı deneyimini iyileştirmek ve hizmetlerin düzgün çalışmasını sağlamak amacıyla çerezler kullanmaktadır.
                            Zorunlu çerezler platformun temel işlevselliği için gereklidir. Analitik çerezler ise kullanım istatistiklerini
                            anlamamıza yardımcı olur.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Haklarınız</h2>
                        <p>6698 sayılı KVKK kapsamında aşağıdaki haklara sahipsiniz:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                            <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                            <li>İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</li>
                            <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                            <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                            <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme</li>
                            <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. İletişim</h2>
                        <p>
                            Gizlilik politikamız hakkında sorularınız veya talepleriniz için bizimle iletişime geçebilirsiniz:
                        </p>
                        <p className="mt-2">
                            <strong>E-posta:</strong> info@co-hr.com.tr<br />
                            <strong>Web:</strong>{' '}
                            <a href="https://co-hr.com.tr" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                co-hr.com.tr
                            </a>
                        </p>
                    </section>
                </div>
            </main>

            <footer className="border-t border-gray-100 bg-gray-50 py-6">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-gray-400 text-sm">
                    Bu bir{' '}
                    <a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-600 hover:text-indigo-500 transition">
                        CO-HR
                    </a>
                    {' '}ürünüdür.
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
